import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface View {
  /** Top-left of the visible area, in metres. */
  x: number;
  y: number;
  /** Pixels per metre. */
  scale: number;
}

interface Options {
  /** Extra space around the plan when fitting, in pixels. */
  padding?: number;
  maxScale?: number;
  /** Pan and zoom enabled (the editor turns panning off while dragging items). */
  enabled?: boolean;
}

/**
 * Pan and zoom for an SVG map measured in metres: mouse wheel and pinch to
 * zoom around the pointer, drag to pan, plus fit/zoom buttons. Clicks that
 * follow a drag are swallowed so dragging the map never selects a table.
 */
export function usePanZoom(contentW: number, contentH: number, opts: Options = {}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<View>({ x: 0, y: 0, scale: 40 });
  const fitted = useRef(false);
  const dragged = useRef(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ startView: View; startDist?: number; startMid?: { x: number; y: number }; start?: { x: number; y: number } } | null>(null);
  const padding = opts.padding ?? 24;
  const enabled = opts.enabled ?? true;

  const fitScale = useCallback(
    (w = size.w, h = size.h) => Math.max(1, Math.min((w - padding * 2) / contentW, (h - padding * 2) / contentH)),
    [size.w, size.h, contentW, contentH, padding],
  );

  const fit = useCallback(() => {
    if (!size.w || !size.h) return;
    const scale = fitScale();
    setView({ scale, x: (contentW - size.w / scale) / 2, y: (contentH - size.h / scale) / 2 });
  }, [size.w, size.h, contentW, contentH, fitScale]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry!.contentRect;
      setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fit on first layout and whenever the plan size changes.
  useEffect(() => {
    fitted.current = false;
  }, [contentW, contentH]);
  useEffect(() => {
    if (!fitted.current && size.w && size.h) {
      fit();
      fitted.current = true;
    }
  }, [size.w, size.h, fit]);

  const clampScale = useCallback(
    (s: number) => Math.min(opts.maxScale ?? 220, Math.max(fitScale() * 0.6, s)),
    [fitScale, opts.maxScale],
  );

  const zoomAt = useCallback(
    (factor: number, px: number, py: number, base?: View) => {
      setView((v0) => {
        const v = base ?? v0;
        const scale = clampScale(v.scale * factor);
        const mx = v.x + px / v.scale;
        const my = v.y + py / v.scale;
        return { scale, x: mx - px / scale, y: my - py / scale };
      });
    },
    [clampScale],
  );

  const zoomBy = useCallback((factor: number) => zoomAt(factor, size.w / 2, size.h / 2), [zoomAt, size.w, size.h]);

  /** Centre the view on a point (metres), optionally zooming in to at least `minScale`. */
  const focus = useCallback(
    (x: number, y: number, minScale?: number) => {
      setView((v) => {
        const scale = minScale ? clampScale(Math.max(v.scale, minScale)) : v.scale;
        return { scale, x: x - size.w / 2 / scale, y: y - size.h / 2 / scale };
      });
    },
    [size.w, size.h, clampScale],
  );

  // Wheel needs a non-passive listener to stop the page scrolling.
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomAt(Math.exp(-delta * (e.ctrlKey ? 0.01 : 0.0018)), e.clientX - rect.left, e.clientY - rect.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt, enabled]);

  const local = (e: React.PointerEvent) => {
    const rect = ref.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enabled || (e.pointerType === 'mouse' && e.button !== 0)) return;
    pointers.current.set(e.pointerId, local(e));
    dragged.current = false;
    const pts = [...pointers.current.values()];
    if (pts.length === 1) gesture.current = { startView: view, start: pts[0] };
    if (pts.length === 2) {
      const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
      gesture.current = {
        startView: view,
        startDist: Math.hypot(a.x - b.x, a.y - b.y),
        startMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!enabled || !pointers.current.has(e.pointerId) || !gesture.current) return;
    pointers.current.set(e.pointerId, local(e));
    const pts = [...pointers.current.values()];
    const g = gesture.current;
    if (pts.length === 1 && g.start) {
      const p = pts[0]!;
      const dx = p.x - g.start.x;
      const dy = p.y - g.start.y;
      if (!dragged.current && Math.hypot(dx, dy) < 5) return;
      if (!dragged.current) {
        dragged.current = true;
        ref.current?.setPointerCapture(e.pointerId);
      }
      setView({ ...g.startView, x: g.startView.x - dx / g.startView.scale, y: g.startView.y - dy / g.startView.scale });
    } else if (pts.length === 2 && g.startDist && g.startMid) {
      dragged.current = true;
      const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const sv = g.startView;
      const scale = clampScale(sv.scale * (dist / g.startDist));
      const mx = sv.x + g.startMid.x / sv.scale;
      const my = sv.y + g.startMid.y / sv.scale;
      setView({ scale, x: mx - mid.x / scale, y: my - mid.y / scale });
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
    else {
      const [p] = [...pointers.current.values()];
      gesture.current = { startView: view, start: p };
    }
  };

  /** True when the last pointer interaction was a drag (so the click should be ignored). */
  const wasDrag = useCallback(() => dragged.current, []);

  const viewBox = size.w && size.h ? `${view.x} ${view.y} ${size.w / view.scale} ${size.h / view.scale}` : `0 0 ${contentW} ${contentH}`;

  return {
    ref,
    size,
    view,
    viewBox,
    scale: view.scale,
    fit,
    zoomBy,
    focus,
    wasDrag,
    /** Map a client (screen) position to plan metres. */
    toPlan: (clientX: number, clientY: number) => {
      const rect = ref.current!.getBoundingClientRect();
      return { x: view.x + (clientX - rect.left) / view.scale, y: view.y + (clientY - rect.top) / view.scale };
    },
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
}
